import { useIntl } from "react-intl";
import { isValidElement, useId, useState } from "react";
import {
  AlertTriangleIcon,
  CheckCircleIcon,
  EyeIcon,
  EyeOffIcon,
} from "./icons";

// JSX port of realnews-web's ui/Input. Keep the two in sync. The only behavioral
// difference: error *objects* here don't route through ErrorMessageView. An
// error *object* with an `id` is resolved against the locale bundle (falling
// back to its `defaultMessage`); one without an id renders `defaultMessage`
// directly (see renderError).

const WIDTH = {
  full: "max-w-full",
  medium: "max-w-sm",
  narrow: "max-w-xxs",
};

/**
 * A string or React element renders as-is; an error object (the
 * `{ defaultMessage, name, ... }` shape the forms pass) renders its message.
 */
function renderError(error, intl) {
  if (!error) return null;
  if (typeof error === "string" || isValidElement(error)) return error;
  if (error.id) {
    return intl.formatMessage({
      id: error.id,
      defaultMessage: error.defaultMessage,
    });
  }
  return error.defaultMessage ?? error.message ?? null;
}

export function Input({
  ref,
  id,
  label,
  required,
  trailing,
  helper,
  error,
  serverError,
  formField = false,
  width = "full",
  wrapperClassName,
  className,
  type,
  value,
  placeholder,
  disabled,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  ...props
}) {
  const intl = useIntl();
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const helperId = useId();
  const errorId = useId();
  const [showPassword, setShowPassword] = useState(false);

  const err = error ?? serverError;
  const hasError = Boolean(err);
  const filled = value != null && value !== "";
  const isPassword = type === "password";

  const describedBy =
    [
      ariaDescribedBy,
      helper && !hasError ? helperId : null,
      hasError ? errorId : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  // Single right-side slot, by precedence: password toggle, then a caller's
  // trailing node, then (formField only) the automatic status icon.
  let rightSide = null;
  if (isPassword) {
    rightSide = (
      <button
        type="button"
        onClick={() => setShowPassword((v) => !v)}
        aria-label={
          showPassword
            ? intl.formatMessage({ id: "template.hide_password", defaultMessage: "Hide password" })
            : intl.formatMessage({ id: "template.show_password", defaultMessage: "Show password" })
        }
        className="shrink-0 cursor-pointer rounded-xs text-text-placeholder-light focus-ring dark:text-text-placeholder-dark"
      >
        {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
      </button>
    );
  } else if (trailing) {
    rightSide = (
      <span className="flex shrink-0 items-center justify-center">
        {trailing}
      </span>
    );
  } else if (formField && hasError) {
    rightSide = (
      <AlertTriangleIcon
        className="shrink-0 text-text-error-light dark:text-text-error-dark"
        size={18}
      />
    );
  } else if (formField && filled) {
    rightSide = (
      <CheckCircleIcon
        className="shrink-0 text-text-success-light dark:text-text-success-dark"
        size={18}
      />
    );
  }

  return (
    <div
      className={`flex w-full flex-col gap-sm ${WIDTH[width]} ${wrapperClassName ?? ""}`}
    >
      {label && (
        <label htmlFor={inputId} className="sr-only">
          {label}
          {required
            ? ` ${intl.formatMessage({ id: "auth.requiredSuffix", defaultMessage: "(required)" })}`
            : ""}
        </label>
      )}
      <div
        className={`flex h-10 items-center gap-xs rounded-xs border px-lg transition-colors
          focus-within:border-text-link-blue-light dark:focus-within:border-text-link-blue-dark
          ${disabled ? "bg-transparent" : "bg-surface-bg-default-light dark:bg-surface-bg-default-dark"}
          ${
            hasError
              ? "border-text-error-light dark:border-text-error-dark"
              : "border-border-edge-strong-light dark:border-border-edge-strong-dark"
          }`}
      >
        <input
          ref={ref}
          id={inputId}
          required={required}
          type={isPassword && showPassword ? "text" : type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={hasError || ariaInvalid}
          {...props}
          className={`min-w-0 flex-1 bg-transparent text-sm font-body leading-sm outline-none
            text-text-default-light dark:text-text-default-dark
            placeholder:text-text-placeholder-light dark:placeholder:text-text-placeholder-dark
            disabled:text-text-disabled-light dark:disabled:text-text-disabled-dark
            focus-visible:outline-2 focus-visible:outline-text-link-blue-light
            [&::-ms-clear]:hidden [&::-ms-reveal]:hidden
            ${className ?? ""}`}
        />
        {formField && filled && placeholder && (
          <span className="max-w-16 shrink-0 truncate text-xs font-body leading-xs text-text-placeholder-light dark:text-text-placeholder-dark">
            {placeholder}
          </span>
        )}
        {rightSide}
      </div>
      {helper && !hasError && (
        <p
          id={helperId}
          className="text-xs font-body leading-xs text-text-default-light dark:text-text-default-dark"
        >
          {helper}
        </p>
      )}
      {hasError && (
        <p
          id={errorId}
          role="alert"
          className="text-xs font-body leading-xs text-text-error-light dark:text-text-error-dark"
        >
          {renderError(err, intl)}
        </p>
      )}
    </div>
  );
}

export default Input;
